import { m as motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { HtmlContent } from "@/components/ui/html-content";

export function HeroSection() {
  const { homeSettings } = useSiteSettings();

  return (
    <section
      className="relative min-h-[70vh] flex items-center justify-center overflow-hidden"
      style={{ contain: "layout style" }}
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/10" />

      {/* Animated background elements */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ contain: "strict" }}
      >
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-primary/5"
            style={{
              width: `${300 + i * 200}px`,
              height: `${300 + i * 200}px`,
              right: `${-100 + i * 50}px`,
              top: `${-50 + i * 100}px`,
            }}
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10 pb-12 md:pb-16">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="h-10 mb-8 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary"
            >
              <Sparkles size={16} />
              <span className="text-sm font-medium">{homeSettings.badge}</span>
            </motion.div>
          </div>

          {/* Headline */}
          <div className="min-h-[180px] md:min-h-[200px] lg:min-h-[180px] xl:min-h-[200px]">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground leading-tight"
            >
              <span className="block">{homeSettings.name}</span>
              <span className="block mt-2 text-primary text-4xl">
                {homeSettings.titleLine1}
              </span>
              <span className="block text-primary text-4xl">
                {homeSettings.titleLine2}
              </span>
            </motion.h1>
          </div>

          {/* Description */}
          <div className="mt-1 md:mt-2 min-h-[56px] md:min-h-[64px]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <HtmlContent
                content={homeSettings.description}
                className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto [&_p]:m-0"
              />
            </motion.div>
          </div>

          {/* CTA Buttons */}
          <div className="mt-8 md:mt-10 min-h-[100px] sm:min-h-[44px]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Button
                asChild
                size="lg"
                className="text-base transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/25"
              >
                <Link to={homeSettings.ctaPrimary.link}>
                  {homeSettings.ctaPrimary.text}
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="text-base transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/10"
              >
                <Link to={homeSettings.ctaSecondary.link}>
                  {homeSettings.ctaSecondary.text}
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
